import { useEffect, useRef, useState } from 'react';
import { philosophyPrinciples } from '@/mocks/homepage';

export default function PhilosophySection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="philosophy" ref={ref} className="bg-sage-100 py-24 md:py-36 relative overflow-hidden">
      {/* Decorative soft shape */}
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-sage-200/40 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-sage-200/30 rounded-full blur-3xl" />

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-5xl mx-auto">
          {/* Section Header */}
          <div
            className={`text-center mb-16 md:mb-24 transition-all duration-1000 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <p className="text-sage-500 text-xs tracking-[0.3em] uppercase mb-4 font-medium">
              Our Philosophy
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-charcoal-300 font-medium leading-tight max-w-3xl mx-auto">
              Five principles for a
              <br />
              <span className="italic font-light">life well-stewarded.</span>
            </h2>
          </div>

          {/* Principles */}
          <div className="space-y-6 md:space-y-8">
            {philosophyPrinciples.map((principle, i) => (
              <div
                key={principle.id}
                className={`flex flex-col md:flex-row items-start gap-5 md:gap-8 bg-white/60 backdrop-blur-sm rounded-2xl p-8 md:p-10 transition-all duration-700 hover:bg-white/80 ${
                  visible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${200 + i * 150}ms` }}
              >
                <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-full bg-sage-200/60 flex items-center justify-center">
                  <i className={`${principle.icon} text-sage-500 text-lg md:text-xl`} />
                </div>
                <div>
                  <h3 className="text-charcoal-300 text-lg md:text-xl font-medium mb-2 md:mb-3">
                    {principle.title}
                  </h3>
                  <p className="text-charcoal-100 text-sm md:text-base leading-relaxed">
                    {principle.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Movement Tagline */}
          <div
            className={`mt-20 md:mt-28 text-center transition-all duration-1000 delay-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="inline-block bg-charcoal-300 text-cream-50 px-8 md:px-12 py-5 md:py-6 rounded-full">
              <p className="font-serif text-lg md:text-2xl font-medium tracking-wide">
                <span className="text-sage-300 italic">Stop listening.</span>{' '}
                <span className="italic font-light">Start practicing.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}